import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, Plus, Edit, Trash2, Save, X, Copy,
  FileText, Check, AlertCircle, Info, ChevronDown, Eye, Send, Smartphone, Loader2, FlaskConical
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationTemplates = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [featureAvailable, setFeatureAvailable] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ type: 'email', recipient: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sendingTestId, setSendingTestId] = useState(null);
  const [previewMode, setPreviewMode] = useState('email'); // 'plain' or 'email'
  const [branding, setBranding] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    event_type: '',
    channel: 'email',
    subject: '',
    body: '',
    is_active: true
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.status === 403) {
        setFeatureAvailable(false);
        setLoading(false);
        return;
      }
      
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEventTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEventTypes(data.event_types || []);
      setPlaceholders(data.placeholders || []);
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchEventTypes();
    // Fetch branding for email preview
    const fetchBranding = async () => {
      try {
        const token = localStorage.getItem('movemate_token');
        const res = await fetch(`${API_URL}/api/notification-templates/email-branding`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setBranding(await res.json());
      } catch (e) { /* branding is optional, plain preview still works */ }
    };
    fetchBranding();
  }, [fetchTemplates, fetchEventTypes]);

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Failed to create template');

      toast.success('Template created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Failed to update template');

      toast.success('Template updated successfully');
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete template');

      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      event_type: '',
      channel: 'email',
      subject: '',
      body: '',
      is_active: true
    });
  };

  const startEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      event_type: template.event_type,
      channel: template.channel,
      subject: template.subject || '',
      body: template.body,
      is_active: template.is_active
    });
  };

  // Load default templates
  const loadDefaultTemplates = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || 'Default templates loaded!');
        fetchTemplates();
      } else {
        toast.error(data.detail || 'Failed to load templates');
      }
    } catch (error) {
      toast.error('Failed to load default templates');
    }
  };

  // Add missing default templates
  const addMissingTemplates = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/add-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || 'Templates added!');
        fetchTemplates();
      } else {
        toast.error(data.detail || 'Failed to add templates');
      }
    } catch (error) {
      toast.error('Failed to add missing templates');
    }
  };

  // Group templates by event category
  const groupTemplatesByCategory = (templateList) => {
    const categoryMap = {};
    const eventTypeToCategory = {};
    
    // Build category map from event types
    eventTypes.forEach(et => {
      eventTypeToCategory[et.id] = et.category || 'Other';
    });
    
    templateList.forEach(template => {
      const category = eventTypeToCategory[template.event_type] || 'Custom';
      if (!categoryMap[category]) {
        categoryMap[category] = [];
      }
      categoryMap[category].push(template);
    });
    
    // Sort categories in a logical order
    const categoryOrder = ['Account', 'Job', 'Estimate', 'Payment', 'Crew', 'Storage', 'Staff', 'Admin', 'Custom', 'Other'];
    const sortedCategories = {};
    
    categoryOrder.forEach(cat => {
      if (categoryMap[cat]) {
        sortedCategories[cat] = categoryMap[cat];
      }
    });
    
    // Add any remaining categories
    Object.keys(categoryMap).forEach(cat => {
      if (!sortedCategories[cat]) {
        sortedCategories[cat] = categoryMap[cat];
      }
    });
    
    return sortedCategories;
  };

  const insertPlaceholder = (placeholder) => {
    setFormData(prev => ({
      ...prev,
      body: prev.body + placeholder
    }));
  };

  // Generate preview with sample data
  const generatePreview = (template) => {
    const companyName = branding?.company_name || 'Your Company Name';
    const supportEmail = branding?.company_email || 'support@yourcompany.com';
    const companyPhone = branding?.company_phone || '+44 123 456 7890';
    const sampleData = {
      '{{company_name}}': companyName,
      '{{company_email}}': supportEmail,
      '{{company_phone}}': companyPhone,
      '{{company_website}}': 'https://yourcompany.com',
      '{{customer_name}}': 'John Smith',
      '{{customer_first_name}}': 'John',
      '{{customer_email}}': 'john.smith@email.com',
      '{{customer_phone}}': '+44 7700 900123',
      '{{job_number}}': 'JOB-2026-001',
      '{{move_date}}': 'January 15, 2026',
      '{{move_time}}': '9:00 AM',
      '{{from_address}}': '123 Main Street, London, SW1A 1AA',
      '{{to_address}}': '456 Oak Avenue, Manchester, M1 1AA',
      '{{pickup_address}}': '123 Main Street, London, SW1A 1AA',
      '{{delivery_address}}': '456 Oak Avenue, Manchester, M1 1AA',
      '{{job_date}}': 'January 15, 2026',
      '{{job_time}}': '9:00 AM',
      '{{total_amount}}': '£450.00',
      '{{deposit_amount}}': '£100.00',
      '{{balance_due}}': '£350.00',
      '{{amount}}': '£450.00',
      '{{driver_name}}': 'Mike Johnson',
      '{{eta}}': '2:30 PM',
      '{{tracking_link}}': 'https://track.example.com/abc123',
      '{{login_code}}': '1234',
      '{{login_link}}': 'https://example.com/login',
      '{{support_email}}': supportEmail,
      '{{phone}}': companyPhone,
      '{{estimate_link}}': 'https://example.com/estimate/abc123',
      '{{estimate_number}}': 'EST-2026-042',
      '{{estimate_valid_until}}': 'February 15, 2026',
      '{{invoice_link}}': 'https://example.com/invoice/abc123',
      '{{invoice_number}}': 'INV-2026-001',
      '{{payment_link}}': 'https://example.com/pay/abc123',
      '{{rating_link}}': 'https://example.com/rate/abc123',
      '{{feedback_link}}': 'https://example.com/feedback/abc123',
      '{{verification_link}}': 'https://example.com/verify/abc123',
      '{{reset_password_link}}': 'https://example.com/reset/abc123',
      '{{storage_location}}': 'Unit 5, Secure Storage Ltd',
      '{{storage_items}}': '3 boxes, 1 wardrobe, 2 chairs',
      '{{storage_start_date}}': 'February 1, 2026',
      '{{storage_monthly_rate}}': '£49.99/month',
      '{{access_date}}': 'February 1, 2026',
      '{{staff_name}}': 'Jane Doe',
      '{{start_date}}': 'January 20, 2026',
    };

    let previewSubject = template.subject || formData.subject || '';
    let previewBody = template.body || formData.body || '';

    Object.entries(sampleData).forEach(([key, value]) => {
      previewSubject = previewSubject.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
      previewBody = previewBody.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    setPreviewContent({ subject: previewSubject, body: previewBody });
    setShowPreview(true);
  };

  // Send instant message
  const handleSendMessage = async () => {
    if (!sendForm.recipient || !sendForm.message) {
      toast.error('Please fill in recipient and message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('movemate_token');
      const endpoint = sendForm.type === 'sms' 
        ? `${API_URL}/api/notifications/sms/send`
        : `${API_URL}/api/notifications/email/send`;

      const payload = sendForm.type === 'sms'
        ? { phone: sendForm.recipient, message: sendForm.message }
        : { email: sendForm.recipient, subject: sendForm.subject, message: sendForm.message };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(`${sendForm.type.toUpperCase()} sent successfully!`);
        setShowSendModal(false);
        setSendForm({ type: 'email', recipient: '', subject: '', message: '' });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Send test template email to admin
  const handleSendTestTemplate = async (templateId) => {
    setSendingTestId(templateId);
    try {
      const token = localStorage.getItem('movemate_token');
      const res = await fetch(`${API_URL}/api/notification-templates/${templateId}/send-test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Test email sent!');
      } else {
        toast.error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTestId(null);
    }
  };

  if (!featureAvailable) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Templates</h1>
          <p className="text-gray-500">Create custom email and SMS templates</p>
        </div>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-8 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-amber-800 mb-2">
              White Label Feature
            </h3>
            <p className="text-amber-600 mb-4">
              Custom notification templates are available on the White Label plan.
            </p>
            <Button onClick={() => window.location.href = '/control-panel/billing'}>
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-templates">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Templates</h1>
          <p className="text-gray-500">Create custom email and SMS templates for automated notifications</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowSendModal(true)} 
            data-testid="quick-send-btn"
          >
            <Send className="mr-2 h-4 w-4" />
            Quick Send
          </Button>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-template-btn">
            <Plus className="mr-2 h-4 w-4" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Templates List */}
      <div className="grid gap-4">
        {templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
              <p className="text-gray-500 mb-4">Get started quickly with our pre-made templates or create your own.</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={loadDefaultTemplates} variant="default" data-testid="load-defaults-btn">
                  <FileText className="mr-2 h-4 w-4" />
                  Load 25 Default Templates
                </Button>
                <Button onClick={() => setShowCreateModal(true)} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Custom
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Default templates include: Welcome emails, job confirmations, reminders, payment receipts, crew notifications, and more.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Add Missing Defaults Button */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={addMissingTemplates}
                data-testid="add-missing-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Missing Defaults
              </Button>
            </div>
            
            {/* Group templates by category */}
            {Object.entries(groupTemplatesByCategory(templates)).map(([category, categoryTemplates]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{category}</h3>
                {categoryTemplates.map((template) => (
            <Card key={template.id} className={!template.is_active ? 'opacity-60' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${template.channel === 'email' ? 'bg-blue-100' : 'bg-green-100'}`}>
                      {template.channel === 'email' ? (
                        <Mail className="h-5 w-5 text-blue-600" />
                      ) : (
                        <MessageSquare className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{template.name}</h3>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline">
                          {eventTypes.find(e => e.id === template.event_type)?.name || template.event_type}
                        </Badge>
                      </div>
                      {template.subject && (
                        <p className="text-sm text-gray-600 mt-1">Subject: {template.subject}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.body}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => generatePreview(template)}
                      title="Preview"
                      data-testid={`preview-template-${template.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSendTestTemplate(template.id)}
                      disabled={sendingTestId === template.id}
                      title="Send test email to admin"
                      data-testid={`send-test-template-${template.id}`}
                    >
                      {sendingTestId === template.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FlaskConical className="h-4 w-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(template)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(template.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</CardTitle>
              <CardDescription>
                {editingTemplate ? 'Update your notification template' : 'Create a new notification template'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Job Confirmation"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label>Channel</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.channel}
                    onChange={(e) => setFormData(prev => ({ ...prev, channel: e.target.value }))}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Event Type</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  value={formData.event_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_type: e.target.value }))}
                >
                  <option value="">Select an event...</option>
                  {eventTypes.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name} - {event.description}
                    </option>
                  ))}
                </select>
              </div>

              {formData.channel === 'email' && (
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="e.g., Your move is confirmed - {{job_number}}"
                    data-testid="template-subject-input"
                  />
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Message Body</Label>
                  <div className="relative group">
                    <Button variant="outline" size="sm">
                      <Info className="h-4 w-4 mr-1" />
                      Placeholders
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                    <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 max-h-60 overflow-y-auto">
                      <div className="p-2">
                        {placeholders.map((p) => (
                          <button
                            key={p.key}
                            className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-sm flex items-center justify-between"
                            onClick={() => insertPlaceholder(p.key)}
                          >
                            <span className="font-mono text-xs">{p.key}</span>
                            <Copy className="h-3 w-3 text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <textarea
                  className="w-full min-h-[150px] px-3 py-2 rounded-md border border-input bg-background resize-y"
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Enter your message here. Use placeholders like {{customer_name}} for dynamic content."
                  data-testid="template-body-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="is_active">Template is active</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTemplate(null);
                    resetForm();
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => generatePreview(formData)}
                  disabled={!formData.body}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </Button>
                <Button onClick={editingTemplate ? handleUpdate : handleCreate} data-testid="save-template-btn">
                  <Save className="mr-2 h-4 w-4" />
                  {editingTemplate ? 'Update' : 'Create'} Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="preview-modal">
            <DialogHeader className="px-6 pt-6 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>Template Preview</DialogTitle>
                  <DialogDescription>
                    See how your message will look to recipients
                  </DialogDescription>
                </div>
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="preview-mode-toggle">
                  <button
                    onClick={() => setPreviewMode('plain')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      previewMode === 'plain'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="preview-mode-plain"
                  >
                    <FileText className="h-3.5 w-3.5 inline mr-1" />
                    Plain
                  </button>
                  <button
                    onClick={() => setPreviewMode('email')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      previewMode === 'email'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="preview-mode-email"
                  >
                    <Mail className="h-3.5 w-3.5 inline mr-1" />
                    Email
                  </button>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {previewMode === 'plain' ? (
                /* Plain text preview */
                <div className="space-y-4 py-2">
                  {previewContent.subject && (
                    <div>
                      <Label className="text-xs text-gray-500">Subject</Label>
                      <p className="font-medium text-gray-900 mt-1">{previewContent.subject}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-gray-500">Message Body</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border whitespace-pre-wrap text-sm">
                      {previewContent.body}
                    </div>
                  </div>
                </div>
              ) : (
                /* Branded email preview */
                <div className="py-2">
                  <div
                    className="rounded-lg overflow-hidden border border-gray-200 shadow-sm"
                    style={{ fontFamily: branding?.font_family === 'System' ? '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' : (branding?.font_family || 'sans-serif') }}
                    data-testid="email-preview-frame"
                  >
                    {/* Email header with branding */}
                    <div
                      className="px-6 py-5 text-center"
                      style={{ backgroundColor: branding?.primary_color || '#3B82F6' }}
                    >
                      {branding?.logo_url ? (
                        <img
                          src={branding.logo_url}
                          alt={branding?.app_name || 'Logo'}
                          className="h-10 mx-auto mb-2"
                          style={{ maxWidth: 180 }}
                        />
                      ) : (
                        <div className="text-white text-lg font-bold tracking-wide">
                          {branding?.app_name || branding?.company_name || 'Your Company'}
                        </div>
                      )}
                    </div>

                    {/* Subject bar */}
                    {previewContent.subject && (
                      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs text-gray-400 mb-0.5">Subject</p>
                        <p className="font-semibold text-gray-800 text-sm">{previewContent.subject}</p>
                      </div>
                    )}

                    {/* Email body */}
                    <div className="px-6 py-6 bg-white">
                      <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                        {previewContent.body}
                      </div>
                    </div>

                    {/* Email footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-medium">
                        {branding?.from_name || branding?.company_name || 'Your Company'}
                      </p>
                      {(branding?.company_address || branding?.company_city) && (
                        <p className="text-xs text-gray-400 mt-1">
                          {[branding.company_address, branding.company_city].filter(Boolean).join(', ')}
                        </p>
                      )}
                      <div className="flex items-center justify-center gap-3 mt-2">
                        {branding?.company_email && (
                          <span className="text-xs text-gray-400">{branding.company_email}</span>
                        )}
                        {branding?.company_phone && (
                          <span className="text-xs text-gray-400">{branding.company_phone}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-300 mt-3">
                        This is a preview. Actual emails may render slightly differently across email clients.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-6 py-3 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setShowPreview(false)} data-testid="preview-close-btn">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick Send Modal */}
      {showSendModal && (
        <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Send Message</DialogTitle>
              <DialogDescription>
                Send a quick SMS or Email to a customer
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Message Type Toggle */}
              <div>
                <Label>Message Type</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant={sendForm.type === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendForm(prev => ({ ...prev, type: 'email' }))}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button
                    variant={sendForm.type === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendForm(prev => ({ ...prev, type: 'sms' }))}
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    SMS
                  </Button>
                </div>
              </div>

              {/* Recipient */}
              <div>
                <Label>{sendForm.type === 'sms' ? 'Phone Number' : 'Email Address'}</Label>
                <Input
                  type={sendForm.type === 'sms' ? 'tel' : 'email'}
                  value={sendForm.recipient}
                  onChange={(e) => setSendForm(prev => ({ ...prev, recipient: e.target.value }))}
                  placeholder={sendForm.type === 'sms' ? '+447...' : 'customer@email.com'}
                  className="mt-1"
                />
              </div>

              {/* Subject (Email only) */}
              {sendForm.type === 'email' && (
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={sendForm.subject}
                    onChange={(e) => setSendForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Message subject..."
                    className="mt-1"
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <Label>Message</Label>
                <textarea
                  className="w-full min-h-[120px] px-3 py-2 mt-1 rounded-md border border-input bg-background resize-y"
                  value={sendForm.message}
                  onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Type your message..."
                />
                {sendForm.type === 'sms' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {sendForm.message.length}/160 characters
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSendModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendMessage} 
                disabled={sending || !sendForm.recipient || !sendForm.message}
              >
                {sending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send {sendForm.type === 'sms' ? 'SMS' : 'Email'}
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default NotificationTemplates;
